import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiPlay, FiMessageCircle } = FiIcons;

const FinalCTASection = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto text-center text-white"
        >
          {/* Main Headline */}
          <h2 className="text-4xl lg:text-7xl font-bold mb-8 leading-tight">
            Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
              Reclaim Your Time
            </span> and Scale Your Practice?
          </h2>
          
          <p className="text-xl lg:text-2xl text-blue-200 mb-12 leading-relaxed max-w-4xl mx-auto">
            The Black Friday offer ends <span className="text-yellow-400 font-bold">November 15th</span>. 
            Lock in your discounted pricing and get a custom-built AI Assistant deployed to your site in just <span className="text-white font-bold">7 days</span>.
          </p>

          {/* Value Props */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <p className="text-4xl font-bold text-yellow-400 mb-2">$400</p>
              <p className="text-blue-200">Discount Savings</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <p className="text-4xl font-bold text-green-400 mb-2">$297</p>
              <p className="text-blue-200">Free Enterprise Plan (3 months)</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <p className="text-4xl font-bold text-purple-400 mb-2">$697</p>
              <p className="text-blue-200">Total Bonus Value</p>
            </div>
          </div>

          {/* Main CTA */}
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 25px 50px rgba(251, 146, 60, 0.5)" }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-orange-500 via-red-500 to-red-600 text-white px-12 py-7 rounded-2xl text-2xl lg:text-3xl font-bold shadow-2xl transition-all duration-300 flex items-center gap-4 mx-auto mb-8 group relative overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
            <span className="relative z-10 flex items-center gap-4">
              <SafeIcon icon={FiArrowRight} className="text-3xl" />
              BOOK MY FREE STRATEGY CALL NOW
              <SafeIcon icon={FiArrowRight} className="text-3xl group-hover:translate-x-2 transition-transform" />
            </span>
          </motion.button>

          {/* Trust Signals */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm mb-12">
            <div className="flex items-center gap-2">
              <span className="text-green-400 text-xl">✓</span>
              <span className="text-blue-200">No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-400 text-xl">✓</span>
              <span className="text-blue-200">7-day setup guarantee</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-400 text-xl">✓</span>
              <span className="text-blue-200">Cancel anytime</span>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-12">
            <div className="flex-1 h-px bg-white/20"></div>
            <span className="text-blue-200 font-semibold">OR</span>
            <div className="flex-1 h-px bg-white/20"></div>
          </div>

          {/* Secondary CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-8"
          >
            <SafeIcon icon={FiMessageCircle} className="text-5xl mx-auto mb-4 text-blue-300" />
            <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
            <p className="text-blue-200 mb-6">Talk to our AI assistant now or book a 15-minute demo to see it in action</p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 flex items-center gap-3 justify-center">
                <SafeIcon icon={FiMessageCircle} className="text-xl" />
                Chat with AI Assistant
              </button>
              <button className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 flex items-center gap-3 justify-center">
                <SafeIcon icon={FiPlay} className="text-xl" />
                Watch 15-Min Demo
              </button>
            </div>
          </motion.div>

          {/* Final Urgency */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-red-300 font-bold text-lg mt-8"
          >
            ⚠️ Black Friday pricing ends November 15th - Only 13 spots remaining
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTASection;